"use client";

import { SchoolData } from "@nimble/candidate-api/types";
import { WhenAllReady } from "@nimble/react-requests/components/shared/when-all-ready";
import { Lock } from "lucide-react";
import { type ReactNode, useCallback, useState } from "react";

import { withAuthGate } from "@/components/shared/auth-paywall/with-auth-gate";
import ButtonLoader from "@/components/shared/button-loader/button-loader";
import {
  type ActionButtonColor,
  actionButtonColorVariants,
  actionButtonSizeVariants,
  ActionButtonVariant,
  actionButtonVariants,
} from "@/components/shared/buttons/button-variants";
import ExpressInterestModal from "@/components/shared/buttons/express-interest-button/express-interest-modal/express-interest-modal";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import useMatchStatus from "@/hooks/data/use-match-status";
import { User } from "@/lib/types/auth";
import { cn } from "@/lib/utils";

const DISABLED_EXPRESS_INTEREST_TOOLTIP_MESSAGE =
  "You have already connected with this school or have an ongoing match.";

type TooltipWrapperProps = {
  suppressTooltip?: boolean;
  children: ReactNode;
  tooltipMessage: string;
};

type ExpressInterestButtonViewProps = {
  testId?: string;
  className?: string;
  variant: ActionButtonVariant;
  color?: ActionButtonColor;
  leadingIcon?: ReactNode;
} & Omit<React.ComponentProps<typeof ButtonLoader>, "variant" | "size">;

type ExpressInterestButtonProps = ExpressInterestButtonViewProps & {
  schoolId: string;
  schoolData: SchoolData;
};

type ExpressInterestButtonGatedProps = ExpressInterestButtonProps & {
  user: User;
  isModalOpen: boolean;
  onCloseModal: () => void;
};

function TooltipWrapper({
  suppressTooltip = false,
  children,
  tooltipMessage,
}: TooltipWrapperProps) {
  if (suppressTooltip) {
    return children;
  }

  return (
    <Tooltip defaultOpen={false}>
      <TooltipTrigger asChild>
        <span>{children}</span>
      </TooltipTrigger>
      <TooltipContent className="max-w-xs text-wrap leading-relaxed">
        {tooltipMessage}
      </TooltipContent>
    </Tooltip>
  );
}

function ExpressInterestButtonView({
  testId,
  className,
  variant,
  color: colorOverride,
  leadingIcon,
  ...viewProps
}: ExpressInterestButtonViewProps) {
  const { size, color } = actionButtonVariants[variant];
  return (
    <ButtonLoader
      variant="default"
      className={cn(
        actionButtonColorVariants[colorOverride ?? color],
        actionButtonSizeVariants[size],
        className,
      )}
      data-testid={testId}
      {...viewProps}
    >
      {leadingIcon}
      Express Interest
    </ButtonLoader>
  );
}

function ExpressInterestButtonContent({
  schoolData,
  isExpressedInterest,
  isModalOpen,
  onCloseModal,
  ...viewProps
}: Omit<ExpressInterestButtonGatedProps, "schoolId" | "user"> & {
  isExpressedInterest: boolean;
}) {
  return (
    <>
      <TooltipWrapper
        suppressTooltip={!isExpressedInterest}
        tooltipMessage={DISABLED_EXPRESS_INTEREST_TOOLTIP_MESSAGE}
      >
        <ExpressInterestButtonView
          disabled={isExpressedInterest}
          {...viewProps}
        />
      </TooltipWrapper>
      {isModalOpen && (
        <ExpressInterestModal
          onClose={onCloseModal}
          onSubmitSuccess={onCloseModal}
          schoolData={schoolData}
          isExpressedInterest={isExpressedInterest}
        />
      )}
    </>
  );
}

function ExpressInterestButtonAuthenticated({
  schoolId,
  user,
  schoolData,
  isModalOpen,
  onCloseModal,
  onClick,
  ...viewProps
}: ExpressInterestButtonGatedProps) {
  const matchStatusQuery = useMatchStatus({
    ncesId: schoolId,
    userId: user.id,
  });

  return (
    <WhenAllReady
      queries={[matchStatusQuery]}
      loader={<ExpressInterestButtonView loading {...viewProps} />}
      ErrorWrapper={() => <ExpressInterestButtonView loading {...viewProps} />}
    >
      <ExpressInterestButtonContent
        schoolData={schoolData}
        isExpressedInterest={
          matchStatusQuery.data?.connection_initiated === true
        }
        isModalOpen={isModalOpen}
        onCloseModal={onCloseModal}
        onClick={onClick}
        {...viewProps}
      />
    </WhenAllReady>
  );
}

const ExpressInterestButtonWithAuthGate = withAuthGate(
  ExpressInterestButtonAuthenticated,
  {
    customLoader: (
      <ExpressInterestButtonView
        loading
        variant={ActionButtonVariant.foregroundSm}
        className="h-10"
      />
    ),
    customPaywall: ({ schoolId: _, ...props }) => (
      <ExpressInterestButtonContent isExpressedInterest={false} {...props} />
    ),
  },
);

export default function ExpressInterestButton({
  schoolId,
  schoolData,
  ...viewProps
}: ExpressInterestButtonProps) {
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleClick = useCallback(() => {
    setIsModalOpen(true);
  }, []);

  const handleClose = useCallback(() => {
    setIsModalOpen(false);
  }, []);

  return (
    <ExpressInterestButtonWithAuthGate
      schoolId={schoolId}
      schoolData={schoolData}
      isModalOpen={isModalOpen}
      onCloseModal={handleClose}
      onClick={handleClick}
      {...viewProps}
    />
  );
}

export function ExpressInterestDormantButton({
  tooltipMessage,
  ...viewProps
}: Pick<ExpressInterestButtonViewProps, "variant" | "testId" | "className"> & {
  tooltipMessage: string;
}) {
  return (
    <TooltipWrapper tooltipMessage={tooltipMessage}>
      <ExpressInterestButtonView
        disabled
        color="foreground"
        leadingIcon={<Lock className="h-4 w-4" />}
        {...viewProps}
      />
    </TooltipWrapper>
  );
}
